import React, { useState, useRef, useEffect } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { ReactComponent as DownArrow } from './../../assets/icons/down-arrow-icon.svg';
import cn from '../../helpers/cn';
import Loader from '../../components/Loader';
import './CommonDropdown.scss';

const CommonDropdown = (props) => {
  const {
    mode = 'single', // 'single' or 'multi'
    enableSearch = false,
    options, // for single, array of options. for multi, each option should include a checked flag
    ddName,
    ddPlaceHolder,
    searchKeys = [],
    labelKeyName, // for single, property key/option itself. for multi, key for option text
    disabled = false,
    containerClass,
    inputClass,
    // single‑select props
    onChangeHandler,
    formLabel,
    formik,
    addAsterisk,
    customDropdownMessage,
    name,
    // multi‑select props
    setOptions,
    enableSelectAll = false,
    defaultSelectAll = false,
    ddOptionsKey,
    ddDisabledMsg = '',
    ddClassName,
    dropDownDisabled = false,
    handleToggle,
  } = props;

  const [searchText, setSearchText] = useState('');
  const [show, setShow] = useState(false);
  const searchInputRef = useRef(null);
  const selectAllRef = useRef(null);
  const menuRef = useRef(null);
  const menuScrollTopRef = useRef(0);

  // Call updateSelectAllCheckboxState whenever options change
  useEffect(() => {
    updateSelectAllCheckboxState();
    if (mode === 'multi' && menuRef.current) {
      menuRef.current.scrollTop = menuScrollTopRef.current;
    }
  }, [options]);

  // reset the search text and focus the search field
  const resetSearchText = (isOpen) => {
    if (enableSearch && isOpen) setSearchText('');
    setShow(isOpen);
    setTimeout(() => {
      if (isOpen && searchInputRef.current) searchInputRef.current.focus();
      if (mode === 'multi') updateSelectAllCheckboxState();
    }, 100);
    if (handleToggle) handleToggle();
  };

  const getFilteredOptions = () => {
    if (!enableSearch || searchText?.length <= 2) return options;
    const lowerSearch = searchText.trim().toLowerCase();

    return options?.filter((option) => {
      if (searchKeys && searchKeys.length) {
        return searchKeys.some((key) => {
          let value = option[key];
          if (
            value === undefined &&
            labelKeyName &&
            option[labelKeyName] !== undefined
          ) {
            value = option[labelKeyName];
          }
          if (value === undefined) {
            value =
              typeof option === 'string' ? option : JSON.stringify(option);
          }
          return value?.toString().toLowerCase().includes(lowerSearch);
        });
      }

      let text =
        typeof option === 'string'
          ? option
          : labelKeyName && option[labelKeyName] !== undefined
          ? option[labelKeyName]
          : JSON.stringify(option);
      return text.toLowerCase().includes(lowerSearch);
    });
  };

  const getSortedOptions = (opts) => {
    if (mode === 'multi') {
      return [...opts].sort((a, b) => {
        if (a.checked === b.checked) return 0;
        return a.checked ? -1 : 1;
      });
    }
    return opts;
  };

  // --- multi-select functions ---
  const onMultiSelectChange = (e, option) => {
    const { checked, defaultValue, id } = e.target;
    menuScrollTopRef.current = menuRef.current?.scrollTop ?? 0;
    let newOptions = [...options];
    if (defaultValue === 'Select All') {
      newOptions = newOptions.map((opt) => {
        if (enableSearch && searchText?.length > 2 && searchKeys?.length > 0) {
          const isVisible = searchKeys.some(
            (key) =>
              opt[key] &&
              opt[key]
                .toString()
                .toLowerCase()
                .includes(searchText.toLowerCase()),
          );
          return isVisible ? { ...opt, checked } : { ...opt };
        }
        return { ...opt, checked };
      });
    } else {
      const objId = id.replace(`${ddName}_`, '');
      const index = newOptions.findIndex(
        (item) => String(item.id) === String(objId),
      );
      if (index !== -1) newOptions[index].checked = checked;
    }
    setOptions && setOptions(newOptions);
  };

  const handleSelectAllChange = (e) => {
    menuScrollTopRef.current = menuRef.current?.scrollTop ?? 0;
    const isChecked = e.target.checked; // Check if "Select All" is checked
    const filteredOptions = getFilteredOptions(); // Get the filtered options (including search results)

    // Update the checked state of all options
    const updatedOptions = options.map((option) => {
      if (filteredOptions.some((filtered) => filtered.id === option.id)) {
        return { ...option, checked: isChecked }; // Update only the filtered options
      }
      return option;
    });

    setOptions(updatedOptions); // Update the state with the new options
  };

  const updateSelectAllCheckboxState = () => {
    if (mode === 'multi' && selectAllRef.current) {
      const filteredOptions = getFilteredOptions();

      if (filteredOptions?.length === 0) {
        // No options available
        selectAllRef.current.checked = false;
        selectAllRef.current.indeterminate = false;
        return;
      }

      const allChecked = filteredOptions?.every((opt) => opt.checked); // All filtered options are checked
      const someChecked = filteredOptions?.some((opt) => opt.checked); // Some filtered options are checked

      if (allChecked) {
        selectAllRef.current.checked = true;
        selectAllRef.current.indeterminate = false;
      } else if (someChecked) {
        selectAllRef.current.checked = false;
        selectAllRef.current.indeterminate = true;
      } else {
        selectAllRef.current.checked = false;
        selectAllRef.current.indeterminate = false;
      }
    }
  };

  const renderSelectAllOption = () => {
    if (mode === 'multi' && enableSelectAll) {
      const filtered = getFilteredOptions();
      return (
        <div
          id={`${ddName}-selectAll`}
          className={cn('add-entitlements-block', {
            'd-none': filtered?.length === 0,
          })}
          key={`${ddName}-selectAll`}
        >
          <div className="add-entitlements-dropdown">
            <input
              ref={selectAllRef}
              type="checkbox"
              id={`${ddName}-selectAll-checkbox`}
              className={cn('search-box', {
                'checkbox-selection-disabled': disabled,
              })}
              defaultValue="Select All"
              onChange={(e) => handleSelectAllChange(e)}
              disabled={disabled}
            />
            <label
              className="add-entitlements-title"
              htmlFor={`${ddName}-selectAll-checkbox`}
            >
              Select All
            </label>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderOptions = () => {
    const filtered = getFilteredOptions();
    const sortedOptions = getSortedOptions(filtered ?? []);
    if (!options) {
      return (
        <h6 className="text-center pt-2">
          <Loader type="scaleLoaderDropdown" />
        </h6>
      );
    }
    if (sortedOptions?.length === 0) {
      return (
        <h6 className="text-center pt-2">
          {enableSearch
            ? 'No results found'
            : customDropdownMessage || 'No results found'}
        </h6>
      );
    }
    if (mode === 'single') {
      return sortedOptions.map((option, i) => {
        if(option) {
          const isDisabled = option.disabled;
          return (
            <div
              className={cn('add-entitlements-block', {
                'disabled-option': isDisabled,
              })}
              key={`${ddName}_${i}`}
              onClick={() => {
                if (!isDisabled) {
                  onChangeHandler && onChangeHandler(option);
                  setShow(false);
                }
              }}
            >
              <div className="account-dropdown">
                <label className="account-label">
                  {option[labelKeyName] ?? option}
                </label>
              </div>
            </div>
          );
        }
      });
    } else if (mode === 'multi') {
      return sortedOptions.map((option, i) => {
        if (option) {
          return (
            <div
              className={cn('add-entitlements-block', {
                'opacity-50': option.disabled,
              })}
              key={`${ddName}_${option?.id}`}
              title={option.disabled ? ddDisabledMsg : ''}
            >
              <div className="add-entitlements-dropdown">
                <input
                  type="checkbox"
                  id={`${ddName}_${option?.id}`}
                  className={cn('search-box', {
                    'checkbox-selection-disabled': disabled,
                  })}
                  checked={option?.checked}
                  onChange={(e) => onMultiSelectChange(e, option)}
                  value={option[ddOptionsKey]}
                  disabled={disabled || option.disabled}
                />
                <label
                  className="add-entitlements-title"
                  htmlFor={`${ddName}_${option?.id}`}
                >
                  {option[labelKeyName]}
                </label>
              </div>
            </div>
          );
        }
      });
    }
  };

  const getToggleLabel = () => {
    if (mode === 'multi') {
      const selectedCount = options?.filter((opt) => opt.checked)?.length || 0;

      if (selectedCount === 0) {
        // No options selected, show only the placeholder
        return ddPlaceHolder;
      }

      // Show the count of selected options
      return `${ddPlaceHolder} (${selectedCount})`;
    }

    return ddPlaceHolder;
  };

  const renderFormLabel = () => {
    if (formLabel) {
      return (
        <>
          <Form.Label className="user-form-label">{formLabel}</Form.Label>
          {addAsterisk && <span className="asterik">*</span>}
        </>
      );
    }
    return null;
  };

  if (mode === 'single') {
    return (
      <Form.Group
        className={`col-lg-6 col-md-12 mb-2 custom-dropdown ${
          containerClass || ''
        }`}
      >
        {renderFormLabel()}
        <Dropdown
          name={name}
          show={!disabled && show}
          onToggle={resetSearchText}
          autoClose="outside"
          className={cn({ 'dropdown-disabled': disabled })}
        >
          <Dropdown.Toggle variant="light">
            <span className="label-truncate">{getToggleLabel()}</span>
            <DownArrow className="user-down-arrow" />
          </Dropdown.Toggle>
          <Dropdown.Menu className={cn('user-form-input', inputClass)}>
            {enableSearch && (
              <div className="add-entitlements-block" key="search">
                <div className="account-dropdown">
                  <input
                    className="w-100 px-2 py-1 search-box"
                    type="text"
                    id="dd-search-input"
                    placeholder="Search"
                    value={searchText}
                    onChange={({ target }) => setSearchText(target.value)}
                    ref={searchInputRef}
                  />
                </div>
              </div>
            )}
            {renderOptions()}
          </Dropdown.Menu>
        </Dropdown>
        {formik && formik.touched?.[name] && formik.errors?.[name] && (
          <div className="form-input-error mt-1">{formik.errors[name]}</div>
        )}
      </Form.Group>
    );
  } else if (mode === 'multi') {
    return (
      <Dropdown
        className={cn(
          'multi-select-dropdown-wrapper',
          ddClassName,
          containerClass,
        )}
        onToggle={resetSearchText}
      >
        <Dropdown.Toggle
          variant="light"
          className="bg-white w-100 text-start shadow-sm"
          disabled={dropDownDisabled}
        >
          {getToggleLabel()} <DownArrow />
        </Dropdown.Toggle>
        <Dropdown.Menu className="m-0" ref={menuRef}>
          {enableSearch && (
            <div className="add-entitlements-block" key="-1">
              <div className="add-entitlements-dropdown">
                <input
                  className="w-100 px-2 py-1 search-box"
                  type="text"
                  id="dd-search-input"
                  placeholder="Search"
                  value={searchText}
                  onChange={({ target }) => setSearchText(target.value)}
                  autoComplete="off"
                  ref={searchInputRef}
                />
              </div>
            </div>
          )}
          {enableSelectAll && renderSelectAllOption()}
          {renderOptions()}
        </Dropdown.Menu>
      </Dropdown>
    );
  }
  return null;
};

export default React.memo(CommonDropdown);
